import { format } from "date-fns";
import moment, { Moment } from "moment";
import sunnyIcon from "@assets/svgs/icon_sunny.svg";
import springIcon from "@assets/svgs/icon_spring.svg";
import winterIcon from "@assets/svgs/icon_heavysnow.svg";
import autumnIcon from "@assets/svgs/icon_fall.svg";

export function getInitials(name: string): string {
  const nameParts = name?.split(" ");
  if (nameParts?.length === 1) {
    return nameParts[0]?.charAt(0)?.toUpperCase();
  } else {
    const initials = nameParts
      ?.slice(0, 2)
      ?.map((part) => part?.charAt(0)?.toUpperCase())
      ?.join("");
    return initials;
  }
}

export const formatDate = (date: string | Date): string => {
  return format(new Date(date), "dd MMM yyyy");
};

export const DOTS = "...";

export const paginationListRange = (
  end: number,
  currentPage: number
): (number | string)[] => {
  const totalPageNumbers = 7;
  const siblingCount = 1;
  const boundaryCount = 2;

  if (end <= totalPageNumbers) {
    return Array.from({ length: end }, (_, idx) => idx + 1);
  }

  // Helper function to check if a number is a boundary number
  const isLeftBoundaryNumber = (page: number) =>
    page <= boundaryCount + siblingCount + 1;

  const isRightBoundaryNumber = (page: number) =>
    page >= end - (boundaryCount + siblingCount);

  // Determine if we should force middle view
  const forceMiddleView =
    (isLeftBoundaryNumber(currentPage) && currentPage > boundaryCount + 1) ||
    (isRightBoundaryNumber(currentPage) && currentPage < end - boundaryCount);

  // If we're forcing middle view, adjust the current page display
  if (forceMiddleView) {
    const middleRange = [
      currentPage - siblingCount,
      currentPage,
      currentPage + siblingCount,
    ];
    return [1, DOTS, ...middleRange, DOTS, end];
  }

  // Regular logic for showing dots
  const shouldShowLeftDots = currentPage - siblingCount > boundaryCount + 1;
  const shouldShowRightDots = currentPage + siblingCount < end - boundaryCount;

  // Case 1: Show dots on right side only
  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftRange = Array.from(
      { length: boundaryCount + siblingCount + 1 },
      (_, idx) => idx + 1
    );
    return [...leftRange, DOTS, end - 1, end];
  }

  // Case 2: Show dots on left side only
  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount = boundaryCount + siblingCount + 1;
    const rightRange = Array.from(
      { length: rightItemCount },
      (_, idx) => end - (rightItemCount - 1) + idx
    );
    return [1, 2, DOTS, ...rightRange];
  }

  // Case 3: Show dots on both sides
  if (shouldShowLeftDots && shouldShowRightDots) {
    const middleRange = [
      currentPage - siblingCount,
      currentPage,
      currentPage + siblingCount,
    ];
    return [1, DOTS, ...middleRange, DOTS, end];
  }

  return Array.from({ length: end }, (_, idx) => idx + 1);
};

export const pricehoursTotal = (price: string, discount: string): string => {
  const priceValue = parseFloat(price);
  const discountValue = parseFloat(discount);

  const validPrice = isNaN(priceValue) ? 0 : priceValue;
  const validDiscount = isNaN(discountValue) ? 0 : discountValue;

  const totalValue = validPrice - validDiscount;
  return totalValue?.toFixed(2);
};

export const getAvailableTimes = (
  options: TimeOption[],
  startFrom: string | null
): TimeOption[] => {
  const index = options?.findIndex((option) => option?.value === startFrom);
  const availableOptions = index >= 0 ? options?.slice(index) : options;
  return availableOptions?.length > 1 ? availableOptions : [];
};

export const formatPhoneNumber = (
  value: string,
  cursorPosition: number
): {
  formattedValue: string;
  newCursorPosition: number;
} => {
  let cleaned = value.replace(/[^\d]/g, "");

  if (!cleaned.startsWith("1")) {
    cleaned = "1" + cleaned;
  }

  // Build the formatted value
  const areaCode = cleaned.substring(1, 4);
  const firstPart = cleaned.substring(4, 7);
  const secondPart = cleaned.substring(7, 11);

  let formattedValue = "+1";
  if (cleaned.length > 1) {
    formattedValue += ` (${areaCode}`;
  }
  if (cleaned.length > 4) {
    formattedValue += `) ${firstPart}`;
  }
  if (cleaned.length > 7) {
    formattedValue += ` ${secondPart}`;
  }

  // Count how many digits before original cursor
  const digitsBeforeCursor = value
    .substring(0, cursorPosition)
    .replace(/[^\d]/g, "").length;

  // Find new cursor position in formatted string
  let digitCount = 0;
  let newCursorPosition = formattedValue.length;

  for (let i = 0; i < formattedValue.length; i++) {
    if (/\d/.test(formattedValue[i])) {
      digitCount++;
      if (digitCount === digitsBeforeCursor) {
        newCursorPosition = i + 1;
        break;
      }
    }
  }

  // Edge cases
  if (digitsBeforeCursor === 0) {
    // Keep cursor after "+1 " instead of jumping to 0
    const prefixEnd = formattedValue.indexOf("(");
    newCursorPosition = prefixEnd > 0 ? prefixEnd + 2 : formattedValue.length;
  } else if (digitsBeforeCursor >= cleaned.length) {
    newCursorPosition = formattedValue.length;
  }

  return { formattedValue, newCursorPosition };
};

export function parseDuration(duration: string): number {
  const [value, unit] = duration.split(" ");
  const numericValue = parseInt(value, 10);

  if (isNaN(numericValue)) {
    throw new Error("Invalid duration format");
  }

  if (unit.toLowerCase().startsWith("minute")) {
    return numericValue;
  } else if (unit.toLowerCase().startsWith("hour")) {
    return numericValue * 60;
  } else {
    throw new Error("Unsupported time unit");
  }
}

export const getWeekdays = (date: moment.Moment): Weekday[] => {
  const weekdays: Weekday[] = [];
  const startOfWeek = date.clone().startOf("isoWeek");

  for (let i = 0; i < 7; i++) {
    weekdays.push(startOfWeek.clone().add(i, "days"));
  }
  return weekdays;
};

export const convertToMinutes = (time: string) => {
  // Handle both 24-hour format (HH:mm) and 12-hour format (h:mm A)
  const parts = time.split(" ");
  const hourMinute = parts[0];
  const period = parts[1]; // undefined for 24-hour format
  
  // eslint-disable-next-line prefer-const
  let [hours, minutes] = hourMinute.split(":").map(Number);
  
  // If period exists, it's 12-hour format - convert to 24-hour
  if (period === "PM" && hours !== 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }
  // If no period, it's already in 24-hour format - no conversion needed
  
  return hours * 60 + minutes;
};

interface FormatValuesProps {
  selectedBays?: number[];
}

export function convertTo12HourFormat(time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  return `${formattedHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
}

export const formatValues = ({ selectedBays }: FormatValuesProps): string => {
  if (!selectedBays || selectedBays?.length === 0) {
    return "";
  }

  const sortedValues = selectedBays?.sort((a, b) => a - b);

  return (
    sortedValues?.slice(0, -1).join(", ") +
    (sortedValues?.length > 1 ? " & " : "") +
    sortedValues[sortedValues?.length - 1]
  );
};

export const formatDateToYearMonthDay = (
  date: string | Date | moment.Moment
): string => {
  return moment(date).format("YYYY-M-D");
};

export const getBillingArray = (
  planId: number,
  membershipData: IMembershipPayload[]
) => {
  const plan = membershipData?.find((item) => item?.id === planId);

  const { billing_frequencies = [] } = plan || {};

  const labelMapping: Record<string, string> = {
    monthly: "Monthly",
    quarterly: "Quarterly",
    bi_annually: "6 Months",
    yearly: "Yearly",
  };

  return (
    billing_frequencies
      ?.filter((freq) => freq.is_active)
      .map((freq) => ({
        label: labelMapping[freq.name],
        value: freq.name,
      })) || []
  );
};

export const formatRenewText = (input: string): string => {
  const mapping: Record<string, string> = {
    monthly: "Month",
    quarterly: "Quarter",
    bi_annually: "6 Months",
    yearly: "Year",
  };

  return mapping[input.toLowerCase()];
};

// export const getBillingArray = (
//   planId: number,
//   membershipData: IMembershipPayload[]
// ) => {
//   const plan = membershipData?.find((item) => item?.id === planId);

//   if (!plan) {
//     throw new Error(`Plan with ID ${planId} not found`);
//   }

//   const { billing } = plan;

//   if (!billing) {
//     throw new Error(`Billing information for plan ID ${planId} not found`);
//   }

//   const labelMapping: Record<string, string> = {
//     monthly: "Monthly",
//     quarterly: "Quarterly",
//     half_year: "Six Month",
//     yearly: "Yearly",
//   };

//   return Object.keys(labelMapping)
//     .filter((key) => billing?.[key as keyof typeof billing]) // Check if key exists
//     .map((key) => ({
//       label: labelMapping[key],
//       value: billing?.[`${key}_id` as keyof typeof billing] ?? null, // Fallback for missing ID
//     }));
// };
//
//

export const getCustomerName = (firstName?: string, lastName?: string) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else if (firstName) {
    return firstName;
  } else if (lastName) {
    return lastName;
  }
  return "";
};

export const formatPriceInput = (value: string): string => {
  const sanitizedValue = value.replace(/[^0-9.,]/g, "");

  const normalizedValue = sanitizedValue.replace(/,/g, "");

  const [integerPart, decimalPart] = normalizedValue.split(".");

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Return the formatted value with the decimal part limited to 2 digits (standard currency format)
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart.slice(0, 2)}`
    : formattedInteger;
};

export function convertTo24HourFormat(time: string): string {
  const timePattern =
    /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?\s*(AM|PM)?$/i;
  const match = time.match(timePattern);

  if (!match) {
    return "Invalid Time";
  }

  const [, hours, minutes, seconds, microseconds, period] = match;
  let parsedHours = parseInt(hours);
  const parsedMinutes = parseInt(minutes);
  const parsedSeconds = seconds ? parseInt(seconds) : undefined;

  if (period) {
    if (period.toUpperCase() === "PM" && parsedHours !== 12) {
      parsedHours += 12;
    } else if (period.toUpperCase() === "AM" && parsedHours === 12) {
      parsedHours = 0;
    }
  }

  const formattedHours = String(parsedHours).padStart(2, "0");
  const formattedMinutes = String(parsedMinutes).padStart(2, "0");
  const formattedSeconds =
    parsedSeconds !== undefined
      ? `:${String(parsedSeconds).padStart(2, "0")}`
      : "";
  const formattedMicroseconds = microseconds
    ? `.${microseconds.padEnd(6, "0")}`
    : "";

  return `${formattedHours}:${formattedMinutes}${formattedSeconds}${formattedMicroseconds}`;
}

export const getISODateTime = (date: string, time: string): string => {
  const formattedDate = moment(date).format("YYYY-MM-DD");
  return `${formattedDate}T${time}`;
};

export const urlToFile = async (
  url: string,
  filename = "event_logo.jpg"
): Promise<File> => {
  const response = await fetch(url);
  const blob = await response.blob();
  const fileType = blob.type || "image/jpeg";
  return new File([blob], filename, { type: fileType });
};
export const exportCsv = (response: string, date: Moment, fileName: string) => {
  const blob = new Blob([response], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const currentDate = moment(date).format("YYYY-MM-DD");
  a.download = `${fileName}_${currentDate}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const isTimeSlotPassed = (
  slotTime: string,
  selectedDate: moment.Moment,
  graceMinutes = 0
) => {
  const today = moment().startOf("day");
  const selectedDay = moment(selectedDate).startOf("day");

  // If selected date is in the future, slot is not passed
  if (selectedDay.isAfter(today)) {
    return false;
  }

  // If selected date is in the past, all slots are passed
  if (selectedDay.isBefore(today)) {
    return true;
  }

  // For today, compare the time with current time minus grace period
  const effectiveCurrentTime = moment().subtract(graceMinutes, "minutes");

  const slotTimeObject = moment(slotTime, "h:mm A");

  const slotMoment = moment(selectedDate).clone().set({
    hour: slotTimeObject.hour(),
    minute: slotTimeObject.minute(),
    second: slotTimeObject.second(),
  });

  // Check if this empty slot's time has already passed
  return slotMoment.isBefore(effectiveCurrentTime);
};

export const generateCompleteHoursArray = (
  apiHours: CalendarViewHours[]
): CalendarViewHours[] => {
  if (!apiHours || apiHours.length === 0) {
    return [];
  }

  const apiHoursMap = new Map<string, CalendarViewHours>();
  apiHours.forEach((hour) => {
    apiHoursMap.set(hour.hour, hour);
  });

  const completeHours: CalendarViewHours[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      const time12 = `${String(displayHour).padStart(2, "0")}:${String(
        minute
      ).padStart(2, "0")} ${ampm}`;

      const existingApiHour = apiHoursMap.get(time12);

      if (existingApiHour) {
        completeHours.push(existingApiHour);
      } else {
        completeHours.push({
          hour: time12,
          bay_blocked_hours: [],
          price: "0.00",
          discount: "0.00",
          is_peak_hour: false,
          is_disabled: true,
        });
      }
    }
  }

  return completeHours;
};

export const getSeasonIcon = (month: string) => {
  const startMonth = moment(month).month() + 1;
  if ([6, 7, 8].includes(startMonth)) return sunnyIcon;
  if ([9, 10, 11].includes(startMonth)) return autumnIcon;
  if ([12, 1, 2].includes(startMonth)) return winterIcon;
  if ([3, 4, 5].includes(startMonth)) return springIcon;

  return sunnyIcon; // Replace this with generic icon when we have it
};

/**
 * Generate all recurring event dates based on frequency, start date, end date, and days of week
 * @param startDate - The start date for recurrence
 * @param endDate - The end date for recurrence
 * @param frequency - daily, weekly, or monthly
 * @param daysOfWeek - Array of day numbers (0=Monday, 6=Sunday) for weekly recurrence
 * @returns Array of date strings in YYYY-MM-DD format
 */
export const generateRecurringDates = (
  startDate: string,
  endDate: string,
  frequency: "daily" | "weekly" | "monthly",
  daysOfWeek?: number[]
): string[] => {
  const dates: string[] = [];
  const start = moment(startDate);
  const end = moment(endDate);
  let current = start.clone();

  // Prevent infinite loops with a max iteration count
  const maxIterations = 1000;
  let iterations = 0;

  while (current.isSameOrBefore(end) && iterations < maxIterations) {
    iterations++;

    if (frequency === "daily") {
      dates.push(current.format("YYYY-MM-DD"));
      current.add(1, "day");
    } else if (frequency === "weekly") {
      // For weekly, check if the current day matches one of the selected days
      if (daysOfWeek && daysOfWeek.length > 0) {
        const currentDayOfWeek = (current.day() + 6) % 7; // Convert Sunday=0 to Monday=0 format
        if (daysOfWeek.includes(currentDayOfWeek)) {
          dates.push(current.format("YYYY-MM-DD"));
        }
      }
      current.add(1, "day");
    } else if (frequency === "monthly") {
      dates.push(current.format("YYYY-MM-DD"));
      current.add(1, "month");
    }
  }

  return dates;
};

// Export error handling utilities
export {
  extractApiErrorMessage,
  getErrorMessage,
  isPermissionError,
  isAuthError,
  isValidationError,
  getErrorStatusCode,
} from "./errorHelpers";
